import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useRepairInvoices } from '../../hooks/useRepairInvoices';
import { repairInvoicesService, RepairInvoice } from '../../services/repairInvoicesService';
import Button from '../../components/ui/Button';
import SearchInput from '../../components/ui/SearchInput';

const RepairInvoicesList: React.FC = () => {
  const { invoices, loading, error, refreshRepairInvoices } = useRepairInvoices();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const filteredInvoices = invoices.filter((invoice: RepairInvoice) => {
    const matchesSearch = invoice.repair_invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (invoice.repair_order?.repair_order_number && invoice.repair_order.repair_order_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (invoice.repair_order?.customer?.name && invoice.repair_order.customer.name.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFilter = filterStatus === 'all' || invoice.status === filterStatus;

    return matchesSearch && matchesFilter;
  });

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this repair invoice?')) {
      try {
        const { error } = await repairInvoicesService.deleteRepairInvoice(id);
        if (error) {
          alert(error.message);
        } else {
          refreshRepairInvoices();
        }
      } catch (err) {
        alert('Failed to delete repair invoice');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Repair Invoices</h1>
        <Link to="/repair-invoices/new">
          <Button variant="primary">
            Create Repair Invoice
          </Button>
        </Link>
      </div>

      <div className="mb-6 flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <SearchInput
            placeholder="Search repair invoices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div>
          <select
            className="input-field"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th className="table-header px-6 py-3">Invoice Number</th>
                <th className="table-header px-6 py-3">Repair Order</th>
                <th className="table-header px-6 py-3">Customer</th>
                <th className="table-header px-6 py-3">Date</th>
                <th className="table-header px-6 py-3">Total Amount</th>
                <th className="table-header px-6 py-3">Status</th>
                <th className="table-header px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInvoices.length === 0 ? (
                <tr>
                  <td colSpan={7} className="table-cell text-center py-8">
                    No repair invoices found
                  </td>
                </tr>
              ) : (
                filteredInvoices.map((invoice: RepairInvoice) => (
                  <tr key={invoice.id}>
                    <td className="table-cell font-medium text-gray-900">
                      {invoice.repair_invoice_number}
                    </td>
                    <td className="table-cell">
                      {invoice.repair_order?.repair_order_number || 'N/A'}
                    </td>
                    <td className="table-cell">
                      {invoice.repair_order?.customer?.name || 'Unknown Customer'}
                    </td>
                    <td className="table-cell">
                      {new Date(invoice.invoice_date).toLocaleDateString()}
                    </td>
                    <td className="table-cell font-medium">
                      {repairInvoicesService.getFormattedTotal(invoice.total_amount)}
                    </td>
                    <td className="table-cell">
                      <span className={`badge ${repairInvoicesService.getStatusBadgeClass(invoice.status)}`}>
                        {repairInvoicesService.getStatusLabel(invoice.status)}
                      </span>
                    </td>
                    <td className="table-cell">
                      <div className="flex space-x-2">
                        <Link
                          to={`/repair-invoices/${invoice.id}`}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          View
                        </Link>
                        {invoice.status === 'draft' && (
                          <>
                            <Link
                              to={`/repair-invoices/${invoice.id}/edit`}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              Edit
                            </Link>
                            <button
                              onClick={() => handleDelete(invoice.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 flex justify-between items-center">
        <div className="text-sm text-gray-500">
          Showing {filteredInvoices.length} of {invoices.length} repair invoices
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" disabled>
            Previous
          </Button>
          <Button variant="outline" disabled>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RepairInvoicesList;